import { Fragment, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { ConversationsBell } from '../messaging/ConversationsBell';
import { buildLabel } from '../../buildInfo';
import {
  LayoutDashboard,
  MessagesSquare,
  Users,
  Calendar,
  CalendarDays,
  MapPin,
  Store,
  Camera,
  DollarSign,
  Settings,
  Wrench,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Monitor,
  Smartphone,
  UserPlus,
  BarChart3,
  FileText,
  FolderOpen,
  UserCog,
  X,
  CheckSquare,
  Search,
  Keyboard,
  Clock,
  Sun,
  Moon,
  SunMoon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { isPlannerOrAbove, SystemRole, hasMinimumRole } from '@peles/shared';
import type { User } from '@peles/shared';
import { useAuth } from '../../context/AuthContext';
import { useViewMode } from '../../context/ViewModeContext';
import { THEME_CYCLE, THEME_LABELS, useTheme } from '../../hooks/useTheme';
import { UserAvatar } from '../common/UserAvatar';
import * as usersApi from '../../api/users';
import { useCommandCenter } from '../command/CommandCenter';
import { isMacPlatform } from '../command/shortcuts';
import { useRecentEvents } from '../../hooks/useRecentEvents';
import { useMyTaskCounts } from '../../hooks/useEventOps';
import { useLocalDay } from '../../hooks/useDashboard';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobile?: boolean;
}

const ROLE_COLORS: Record<string, string> = {
  ADMIN: 'bg-red-500/20 text-red-300',
  PLANNER: 'bg-primary-500/20 text-primary-300',
  CLIENT: 'bg-green-500/20 text-green-300',
  VENDOR: 'bg-yellow-500/20 text-yellow-300',
};

const navItems = [{ to: '/', label: 'Dashboard', icon: LayoutDashboard }];

const clientPortalNavItems = [
  { to: '/client', label: 'My Events', icon: Calendar },
  { to: '/photos', label: 'Photos', icon: Camera },
];

const vendorPortalNavItems = [{ to: '/vendor', label: 'Vendor Dashboard', icon: Store }];

const clientNavItems = [{ to: '/photos', label: 'Photos', icon: Camera }];

const plannerNavItems = [
  { to: '/users', label: 'Users', icon: Users },
  { to: '/events', label: 'Events', icon: Calendar },
  { to: '/calendar', label: 'Calendar', icon: CalendarDays },
  { to: '/venues', label: 'Venues', icon: MapPin },
  { to: '/vendors', label: 'Vendors', icon: Store },
  { to: '/expenses', label: 'Expenses', icon: DollarSign },
  { to: '/guests', label: 'Guests', icon: UserPlus },
  { to: '/communications', label: 'Communication', icon: MessagesSquare },
  { to: '/tasks', label: 'Tasks', icon: CheckSquare },
  { to: '/files', label: 'Files', icon: FolderOpen },
  { to: '/templates', label: 'Templates', icon: FileText },
  { to: '/reports', label: 'Reports', icon: BarChart3 },
];

// Inquiries sit under Communication, and the mail log, audit log, backups and
// SQL client are one System page with a tab each.
const adminNavItems = [
  { to: '/admin', label: 'System', icon: Wrench },
  { to: '/settings', label: 'Settings', icon: Settings },
];

function ImpersonateModal({ onClose }: { onClose: () => void }) {
  const { impersonate } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [isPending, setIsPending] = useState(false);

  const { data: users = [], isLoading } = useQuery<User[]>({
    queryKey: ['users'],
    queryFn: () => usersApi.listUsers({ isActive: true }),
  });

  const filtered = users.filter(
    (u) =>
      u.systemRole !== SystemRole.ADMIN &&
      (u.name.toLowerCase().includes(search.toLowerCase()) ||
        (u.email ?? '').toLowerCase().includes(search.toLowerCase())),
  );

  async function handleSelect(userId: string) {
    setIsPending(true);
    try {
      await impersonate(userId);
      onClose();
      // The impersonated user may not have access to the current (admin-only)
      // page — always land on the main page after switching identity
      navigate('/');
    } finally {
      setIsPending(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Impersonate User"
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Impersonate User</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
        <div className="p-4 border-b">
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            autoFocus
          />
        </div>
        <div className="overflow-y-auto flex-1">
          {isLoading ? (
            <p className="p-4 text-sm text-gray-500">Loading users...</p>
          ) : filtered.length === 0 ? (
            <p className="p-4 text-sm text-gray-500">No users found.</p>
          ) : (
            <ul>
              {filtered.map((u) => (
                <li key={u.id}>
                  <button
                    onClick={() => handleSelect(u.id)}
                    disabled={isPending}
                    className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    <UserAvatar user={u} size="sm" />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">{u.name}</span>
                      {u.email && <span className="text-xs text-gray-500">{u.email}</span>}
                      <span className="text-xs text-gray-400">{u.systemRole}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="p-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 border rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export function Sidebar({ collapsed, onToggle, mobile }: SidebarProps) {
  const { user, logout, isImpersonating, realUser, stopImpersonation } = useAuth();
  const { isMobile, toggleViewMode } = useViewMode();
  const navigate = useNavigate();
  const [showImpersonateModal, setShowImpersonateModal] = useState(false);
  const commands = useCommandCenter();
  const { events: recentEvents } = useRecentEvents();
  const isClientRole = user?.systemRole === SystemRole.CLIENT;
  const isVendorRole = user?.systemRole === SystemRole.VENDOR;
  const showClientLinks = user && hasMinimumRole(user.systemRole, SystemRole.CLIENT);
  const showPlannerLinks = user && isPlannerOrAbove(user.systemRole);
  const { date: todayKey } = useLocalDay();
  const { data: myTasks } = useMyTaskCounts(todayKey, !!showPlannerLinks);
  // My own tasks that need doing now: overdue or due today.
  const tasksDueNow = (myTasks?.counts.overdue ?? 0) + (myTasks?.counts.today ?? 0);
  const showAdminLinks = user && hasMinimumRole(user.systemRole, SystemRole.ADMIN);
  // Show impersonate button only when the real (non-impersonated) admin is logged in
  const effectiveUser = isImpersonating ? realUser : user;
  const showImpersonateButton =
    !isImpersonating && effectiveUser && hasMinimumRole(effectiveUser.systemRole, SystemRole.ADMIN);

  // In mobile mode, always show expanded and close drawer on nav
  const isCollapsed = mobile ? false : collapsed;
  const onNavClick = mobile ? onToggle : undefined;

  return (
    <>
      {showImpersonateModal && <ImpersonateModal onClose={() => setShowImpersonateModal(false)} />}
      <aside
        className={`${isCollapsed ? 'w-16' : 'w-64'} theme-light bg-gray-900 text-gray-100 flex flex-col transition-all duration-200 shrink-0 h-full`}
      >
        {/* Impersonation banner */}
        {isImpersonating &&
          (isCollapsed ? (
            <div
              className="flex justify-center items-center py-2 bg-amber-500"
              title={`Impersonating: ${user?.name ?? ''}`}
            >
              <UserCog size={16} className="text-white" />
            </div>
          ) : (
            <div className="flex items-center justify-between px-3 py-2 bg-amber-500 text-white text-xs font-medium">
              <div className="flex items-center gap-1.5 min-w-0">
                <UserCog size={14} className="shrink-0" />
                <span className="truncate">Impersonating: {user?.name ?? ''}</span>
              </div>
              <button
                onClick={() => {
                  stopImpersonation();
                  // Return to the main page — the current page may belong to
                  // the impersonated user's context
                  navigate('/');
                }}
                className="ml-2 shrink-0 text-white/80 hover:text-white"
                aria-label="Stop impersonation"
                title="Stop impersonation"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          {!isCollapsed ? (
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="NP Events" className="h-8 w-8 rounded bg-white p-0.5" />
              <span className="text-sm font-bold leading-tight">
                NP Events
                <br />
                <span className="text-xs font-normal text-gray-400">&amp; Productions</span>
              </span>
            </div>
          ) : (
            <img src="/logo.png" alt="NP Events" className="h-8 w-8 rounded bg-white p-0.5" />
          )}
          {!mobile && (
            <button
              onClick={onToggle}
              className="text-gray-400 hover:text-white p-1"
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </button>
          )}
        </div>

        <nav className="flex-1 py-4 overflow-y-auto">
          {commands.available && (
            <div className={isCollapsed ? 'px-2 pb-3' : 'px-3 pb-3'}>
              <button
                type="button"
                onClick={() => {
                  commands.openSearch();
                  onNavClick?.();
                }}
                aria-label="Search"
                title={isCollapsed ? `Search (${isMacPlatform() ? '⌘' : 'Ctrl+'}K)` : undefined}
                className={`w-full flex items-center rounded-lg bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700 transition-colors text-sm ${
                  isCollapsed ? 'justify-center py-2.5' : 'gap-2 px-3 py-2'
                }`}
              >
                <Search size={16} />
                {!isCollapsed && (
                  <>
                    <span className="flex-1 text-left">Search…</span>
                    <kbd className="text-[10px] font-semibold text-gray-500 border border-gray-600 rounded px-1.5 py-0.5">
                      {isMacPlatform() ? '⌘K' : 'Ctrl K'}
                    </kbd>
                  </>
                )}
              </button>
            </div>
          )}
          {navItems.map((item) => (
            <SidebarLink key={item.to} {...item} collapsed={isCollapsed} onNavClick={onNavClick} />
          ))}
          {isClientRole &&
            clientPortalNavItems.map((item) => (
              <SidebarLink
                key={item.to}
                {...item}
                collapsed={isCollapsed}
                onNavClick={onNavClick}
              />
            ))}
          {isVendorRole &&
            vendorPortalNavItems.map((item) => (
              <SidebarLink
                key={item.to}
                {...item}
                collapsed={isCollapsed}
                onNavClick={onNavClick}
              />
            ))}
          {showClientLinks &&
            !isClientRole &&
            clientNavItems.map((item) => (
              <SidebarLink
                key={item.to}
                {...item}
                collapsed={isCollapsed}
                onNavClick={onNavClick}
              />
            ))}
          {showPlannerLinks &&
            plannerNavItems.map((item) => (
              <Fragment key={item.to}>
                <SidebarLink
                  {...item}
                  collapsed={isCollapsed}
                  onNavClick={onNavClick}
                  badge={item.to === '/tasks' ? tasksDueNow : undefined}
                  badgeLabel="of your tasks overdue or due today"
                />
                {/* The events opened lately, under Events itself: one click back. */}
                {item.to === '/events' && !isCollapsed && recentEvents.length > 0 && (
                  <div aria-label="Recent events" className="pb-2">
                    <div className="pl-12 pr-4 pt-1 pb-0.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      <Clock size={11} />
                      Recent
                    </div>
                    {recentEvents.map((e) => (
                      <NavLink
                        key={e.id}
                        to={`/events/${e.id}`}
                        end
                        onClick={onNavClick}
                        title={e.name}
                        className={({ isActive }) =>
                          `block pl-12 pr-4 py-1 text-[13px] truncate transition-colors ${
                            isActive
                              ? 'text-white'
                              : 'text-gray-400 hover:text-white hover:bg-gray-800'
                          }`
                        }
                      >
                        {e.name}
                      </NavLink>
                    ))}
                  </div>
                )}
              </Fragment>
            ))}
          {showAdminLinks &&
            adminNavItems.map((item) => (
              <SidebarLink
                key={item.to}
                {...item}
                collapsed={isCollapsed}
                onNavClick={onNavClick}
              />
            ))}
        </nav>

        {/*
          The account bar.

          ⚠️ **An icon row, not five full-width buttons.** Every action here is
          reached rarely and recognised instantly — a bell, a logout arrow —
          so each was costing a whole line of a rail that the navigation above
          actually needs. Collapsed, the row stacks, because six icons do not
          fit in a 64px rail.

          The bell is first and lives here rather than in the header because
          its dropdown is a *list*: it wants to open beside the navigation it
          belongs to, and a bell in the top-right of a wide screen puts the
          reader's conversations as far from the rest of the app as the layout
          allows.
        */}
        {user && (
          <div className="border-t border-gray-700 p-3">
            <button
              onClick={() => {
                navigate('/profile');
                onNavClick?.();
              }}
              className={`flex items-center w-full rounded-md px-2 py-2 text-sm text-gray-300 hover:bg-gray-800 hover:text-white transition-colors ${isCollapsed ? 'justify-center' : 'gap-3'}`}
              title={isCollapsed ? user.name : undefined}
            >
              <UserAvatar user={user} size={isCollapsed ? 'sm' : 'md'} />
              {!isCollapsed && (
                <div className="flex flex-col items-start overflow-hidden min-w-0">
                  <span className="flex items-center gap-1.5 w-full min-w-0">
                    <span className="truncate font-medium">{user.name}</span>
                    {/*
                      Kept, though the reference has only the address: while
                      impersonating, the role is the one thing on this bar that
                      says whose session this is.
                    */}
                    <span
                      className={`shrink-0 text-[9px] px-1.5 py-0.5 rounded-full font-medium ${ROLE_COLORS[user.systemRole] || 'bg-gray-700 text-gray-300'}`}
                    >
                      {user.systemRole}
                    </span>
                  </span>
                  <span className="truncate w-full text-left text-xs text-gray-500">
                    {user.email}
                  </span>
                </div>
              )}
            </button>

            <div className={`mt-2 flex items-center ${isCollapsed ? 'flex-col gap-1' : 'gap-1'}`}>
              <ConversationsBell />
              {commands.available && !mobile && (
                <button
                  onClick={commands.openHelp}
                  className="rounded-md p-2 text-gray-400 hover:bg-gray-800 hover:text-white transition-colors"
                  title="Keyboard shortcuts (?)"
                  aria-label="Keyboard shortcuts"
                >
                  <Keyboard size={18} />
                </button>
              )}
              {showImpersonateButton && (
                <button
                  onClick={() => setShowImpersonateModal(true)}
                  className="rounded-md p-2 text-gray-400 hover:bg-gray-800 hover:text-white transition-colors"
                  title="Impersonate User"
                  aria-label="Impersonate User"
                >
                  <UserCog size={18} />
                </button>
              )}
              <button
                onClick={toggleViewMode}
                className="rounded-md p-2 text-gray-400 hover:bg-gray-800 hover:text-white transition-colors"
                title={isMobile ? 'Desktop View' : 'Mobile View'}
                aria-label={isMobile ? 'Desktop View' : 'Mobile View'}
              >
                {isMobile ? <Monitor size={18} /> : <Smartphone size={18} />}
              </button>
              <ThemeButton />
              {/*
                Last, and pushed away from the others when there is room: it is
                the one action on this row that cannot be undone by clicking
                again.
              */}
              <button
                onClick={() => {
                  logout();
                  onNavClick?.();
                }}
                className={`rounded-md p-2 text-gray-400 hover:bg-gray-800 hover:text-white transition-colors ${isCollapsed ? '' : 'ml-auto'}`}
                title="Logout"
                aria-label="Logout"
              >
                <LogOut size={18} />
              </button>
            </div>

            {!isCollapsed && (
              /*
               * Which build is being looked at. The first question of any bug
               * report, and the one nobody can answer from the UI otherwise —
               * "did my fix deploy?" was being answered by reading CloudFront.
               */
              <p className="mt-2 text-[10px] text-gray-600" data-testid="build-version">
                {buildLabel()}
              </p>
            )}
          </div>
        )}
      </aside>
    </>
  );
}

function SidebarLink({
  to,
  label,
  icon: Icon,
  collapsed,
  onNavClick,
  badge,
  badgeLabel,
}: {
  to: string;
  label: string;
  icon: LucideIcon;
  collapsed: boolean;
  onNavClick?: () => void;
  /** A count to call out beside the link; nothing is shown for 0. */
  badge?: number;
  badgeLabel?: string;
}) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      onClick={onNavClick}
      className={({ isActive }) =>
        `flex items-center px-4 py-3 text-sm transition-colors ${
          isActive
            ? 'bg-gray-800 text-white border-r-2 border-primary-500'
            : 'text-gray-400 hover:bg-gray-800 hover:text-white'
        }`
      }
    >
      <span className="relative">
        <Icon size={20} />
        {!!badge && collapsed && (
          <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500" aria-hidden />
        )}
      </span>
      {!collapsed && <span className="ml-3">{label}</span>}
      {!!badge && (
        <span
          className={`${collapsed ? 'sr-only' : 'ml-auto'} min-w-[1.25rem] px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[11px] font-semibold text-center`}
          aria-label={`${badge} ${badgeLabel ?? ''}`.trim()}
        >
          {badge}
        </span>
      )}
    </NavLink>
  );
}

const THEME_ICONS = { light: Sun, dark: Moon, system: SunMoon } as const;

/**
 * Light → Dark → Match system, one press each. The icon is the current choice
 * and the label says what the next press does, because a toggle whose icon
 * shows the *next* state is the one everybody reads backwards.
 */
function ThemeButton() {
  const { preference, cycle } = useTheme();
  const Icon = THEME_ICONS[preference];
  const next = THEME_CYCLE[(THEME_CYCLE.indexOf(preference) + 1) % THEME_CYCLE.length];
  const label = `Theme: ${THEME_LABELS[preference]}. Switch to ${THEME_LABELS[next]}`;
  return (
    <button
      type="button"
      onClick={cycle}
      className="rounded-md p-2 text-gray-400 hover:bg-gray-800 hover:text-white transition-colors"
      title={label}
      aria-label={label}
    >
      <Icon size={18} />
    </button>
  );
}
